import { useCallback, useState } from "react";
import { indexedDBService } from "../../../common/utils/indexedDB";
import { PersonInfoInterface } from "../../../common/interfaces/PersonInfoInterface";

// Define types for the data structures
interface DataItem {
    id: string;
    content: unknown;
    timestamp: number;
    synced: boolean;
}

interface VoteDataItem {
    id: string;
    content: PersonInfoInterface;
    timestamp: number;
    synced: boolean;
}

interface CsvRowData {
    choice: string;
    name: string;
    email: string;
    company: string;
}

interface ExportToCSVReturnType {
    csvData: CsvRowData[];
    showCSVLink: boolean;
    csvHeaders: { label: string; key: string }[];
    handleLogoClick: () => void;
}

export const useExportToCsv = (): ExportToCSVReturnType => {
    const [clickCount, setClickCount] = useState(0);
    const [csvData, setCsvData] = useState<CsvRowData[]>([]);
    const [showCSVLink, setShowCSVLink] = useState(false);

    const validateExportCode = (inputCode: string | null): boolean => {
        const correctCode = import.meta.env.VITE_EXPORT_CODE || "";
        return inputCode === correctCode;
    };

    const filterVoteData = (allData: DataItem[]): VoteDataItem[] => {
        return allData.filter(
            (item) =>
                item.id.includes("vote_") ||
                (item.content &&
                    typeof item.content === "object" &&
                    JSON.stringify(item.content).includes("vote_")),
        ) as VoteDataItem[];
    };

    const prepareCsvData = (voteData: VoteDataItem[]): CsvRowData[] => {
        return voteData.map((item) => {
            const personInfo = item.content;
            return {
                choice: personInfo?.choice || "",
                name: personInfo?.name || "",
                email: personInfo?.email || "",
                company: personInfo?.company || "",
            };
        });
    };

    const triggerCsvDownload = (): void => {
        setTimeout(() => {
            const csvLinkElement = document.querySelector(
                ".csv-download-link",
            ) as HTMLElement;
            if (csvLinkElement) {
                csvLinkElement.click();
            }
        }, 100);
    };

    const handleExportProcess = useCallback(async (): Promise<void> => {
        try {
            const allData = await indexedDBService.getAllData();
            const voteData = filterVoteData(allData);

            if (voteData.length === 0) {
                alert("No vote_ data found in IndexedDB");
                return;
            }

            const csvRows = prepareCsvData(voteData);
            setCsvData(csvRows);
            setShowCSVLink(true);
            triggerCsvDownload();
        } catch (error) {
            console.error("Error retrieving data:", error);
            alert("An error occurred while retrieving the data");
        }
    }, [setCsvData, setShowCSVLink]);

    const handleCodeValidation = useCallback(async (): Promise<void> => {
        const staticCode = prompt("Fill in the code to export the data:");

        if (staticCode === null) {
            setClickCount(0);
            return;
        }

        if (!validateExportCode(staticCode)) {
            alert("Incorrect code!");
            setClickCount(0);
            return;
        }

        await handleExportProcess();
    }, [setClickCount, handleExportProcess]);

    const resetClickCounter = (): void => {
        setTimeout(() => {
            setClickCount(0);
        }, 2000);
    };

    const handleLogoClick = useCallback(async () => {
        const newClickCount = clickCount + 1;
        setClickCount(newClickCount);

        if (newClickCount !== 5) {
            resetClickCounter();
            return;
        }

        await handleCodeValidation();
        setClickCount(0);
        resetClickCounter();
    }, [clickCount, handleCodeValidation]);

    const csvHeaders = [
        { label: "Chosen color:", key: "choice" },
        { label: "Name:", key: "name" },
        { label: "Email address:", key: "email" },
        { label: "Company name:", key: "company" },
    ];

    return {
        csvData,
        showCSVLink,
        csvHeaders,
        handleLogoClick,
    };
};
